import React from "react";
import {
  useGetCenterUsersQuery,
  useSaveUserCredentialsMutation,
  useDeleteUserMutation,
  useUpdateUserMutation,
} from "../api/usersApi";
import { UserDetails } from "../types/userResponse";
import {
  UserCredentialsPayload,
  UserUpdatePayload,
} from "../types/userRequest";
import { getApiErrorMessage } from "../../../../../core/utils/errorHandler";

export const useUsers = (params?: {
  centerId?: number;
  page?: number;
  size?: number;
  search?: string;
}) => {
  const [
    saveUserCredentialsMutation,
    { isLoading: isSaving, isError: isSaveError, error: saveError },
  ] = useSaveUserCredentialsMutation();

  const { data, isLoading, isError, error } = useGetCenterUsersQuery(
    {
      centerId: params?.centerId || 0,
      page: params?.page || 1,
      size: params?.size || 10,
    },
    {
      skip: !params?.centerId,
    }
  );

  const [deleteUserMutation, { isLoading: isDeleting }] =
    useDeleteUserMutation();

  const [updateUserMutation, { isLoading: isUpdating }] =
    useUpdateUserMutation();

  const getUsersState = () => {
    if (isLoading) {
      return { users: [], isLoading: true, error: null };
    }

    if (isError) {
      return {
        users: [],
        isLoading: false,
        error: getApiErrorMessage(error),
      };
    }

    return {
      users: data?.data?.items || [],
      isLoading: false,
      error: null,
    };
  };

  const saveUserCredentials = async (payload: UserCredentialsPayload) => {
    try {
      if (!payload.status) {
        payload.status = "active";
      }
      const response = await saveUserCredentialsMutation(payload).unwrap();
      return response;
    } catch (error) {
      throw error;
    }
  };

  const deleteUser = async (id: number) => {
    try {
      const response = await deleteUserMutation(id).unwrap();
      return response;
    } catch (error) {
      throw error;
    }
  };

  const deactivateUser = async (id: number) => {
    try {
      const response = await updateUserMutation({ id, payload: { status: "inactive" } }).unwrap();
      return response;
    } catch (error) {
      throw error;
    }
  };

  const activateUser = async (id: number) => {
    try {
      const response = await updateUserMutation({ id, payload: { status: "active" } }).unwrap();
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateUser = async (id: number, payload: UserUpdatePayload) => {
    try {
      if (!payload.status) {
        payload.status = "active";
      }
      const response = await updateUserMutation({ id, payload }).unwrap();
      return response;
    } catch (error) {
      throw error;
    }
  };

  const paginationData = data?.data
    ? {
        totalPages: data.data.totalPages,
        pageSize: data.data.pageSize,
        totalItems: data.data.totalItems,
        currentPage: data.data.currentPage,
      }
    : null;

  return {
    getUsersState,
    saveUserCredentials,
    isSaving,
    saveError: isSaveError ? getApiErrorMessage(saveError) : null,
    paginationData,
    deleteUser,
    isDeleting,
    updateUser,
    isUpdating,
    deactivateUser,
    activateUser,
  };
};
